import React, { useMemo } from 'react';
import { Box, Group, Button, ActionIcon, Tooltip, Text, Badge } from '@mantine/core';
import {
  IconDownload,
  IconEye,
  IconFile,
  IconPhoto,
  IconPdf,
  IconFileText
} from '@tabler/icons-react';
import { FileAttachmentProps } from '../../types/assistant';
import classes from './FileAttachment.module.css';

/**
 * FileAttachment Component
 * Displays file attachments with download and preview options
 */
export const FileAttachment: React.FC<FileAttachmentProps> = ({
  file,
  onDownload,
  onPreview,
}) => {
  const { id, name, type, size, url } = file;

  // Determine file icon based on MIME type
  const fileIcon = useMemo(() => {
    if (type.startsWith('image/')) return <IconPhoto size={20} />;
    if (type === 'application/pdf') return <IconPdf size={20} />;
    if (type.startsWith('text/')) return <IconFileText size={20} />;
    return <IconFile size={20} />;
  }, [type]);

  // Format file size for display
  const formattedSize = useMemo(() => {
    const bytes = size;
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }, [size]);

  // Determine if preview is supported
  const supportsPreview = useMemo(() => {
    return type.startsWith('image/') || type === 'application/pdf';
  }, [type]);

  const handleDownload = () => {
    if (onDownload) {
      onDownload(id);
    } else if (url) {
      // Fallback: direct download
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview(id);
    } else if (supportsPreview && url) {
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  return (
    <Box className={classes.fileAttachment}>
      <Group justify="space-between" align="center" wrap="nowrap">
        {/* File info */}
        <Group gap="sm" align="flex-start" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.fileIcon}>{fileIcon}</div>
          <div className={classes.fileInfo} style={{ minWidth: 0 }}>
            <Tooltip label={name} multiline maw={300}>
              <Text size="sm" fw={500} truncate className={classes.fileName}>
                {name}
              </Text>
            </Tooltip>
            <Group gap={4} wrap="nowrap">
              <Badge size="xs" variant="light" title={type} style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {type.split('/')[1] ?? type.split('/')[0] ?? type}
              </Badge>
              <Text size="xs" c="dimmed">
                {formattedSize}
              </Text>
            </Group>
          </div>
        </Group>

        {/* Actions */}
        <Group gap={4} wrap="nowrap" className={classes.actions}>
          {supportsPreview && (
            <Tooltip label="Vorschau" withArrow>
              <ActionIcon
                size="sm"
                variant="light"
                onClick={handlePreview}
                aria-label="Datei anzeigen"
              >
                <IconEye size={16} />
              </ActionIcon>
            </Tooltip>
          )}

          <Tooltip label="Herunterladen" withArrow>
            <ActionIcon
              size="sm"
              variant="light"
              onClick={handleDownload}
              aria-label="Datei herunterladen"
            >
              <IconDownload size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Box>
  );
};

FileAttachment.displayName = 'FileAttachment';
